import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, RotateCw, Send, XCircle } from 'lucide-react';
import { invitationLink } from '../../lib/invitation-link.js';
import type { Invitation } from '../../generated/api/types.gen.js';
import {
  listOrgInvitationsOptions,
  listOrgInvitationsQueryKey,
  resendInvitationMutation,
  revokeInvitationMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Button, Callout, DataTable, EmptyState, useToast } from '../../components/ui/index.js';
import type { BadgeVariant, DataTableColumn } from '../../components/ui/index.js';
import { Can } from '../../components/auth/Can.js';
import { ASSIGNABLE_ROLES, formatDate, roleLabel } from './user-labels.js';

interface InvitationListProps {
  orgId: number;
}

const STATUS: Record<Invitation['status'], { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Waiting for reply', variant: 'warning' },
  accepted: { label: 'Accepted', variant: 'success' },
  expired: { label: 'Expired', variant: 'neutral' },
  revoked: { label: 'Revoked', variant: 'danger' },
};

/** Invitations sent for the organization, with resend and revoke for the ones still waiting. */
export const InvitationList: React.FC<InvitationListProps> = ({ orgId }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const path = { orgId: String(orgId) };

  const { data, isPending, isError, refetch } = useQuery(listOrgInvitationsOptions({ path }));
  const refresh = () => queryClient.invalidateQueries({ queryKey: listOrgInvitationsQueryKey({ path }) });

  const resend = useMutation({
    ...resendInvitationMutation(),
    onSuccess: () => {
      // Resending issues a new link (the old one stops working).
      notify('Invitation renewed: copy its new link');
      return refresh();
    },
    onError: () => notify('Could not resend the invitation', 'danger'),
  });
  const revoke = useMutation({
    ...revokeInvitationMutation(),
    onSuccess: () => {
      notify('Invitation revoked');
      return refresh();
    },
    onError: () => notify('Could not revoke the invitation', 'danger'),
  });

  // No email provider yet: the inviter sends the link themselves. It works only for the invited email.
  const copyLink = async (token: string) => {
    try {
      await navigator.clipboard.writeText(invitationLink(token));
      notify('Invitation link copied');
    } catch {
      notify('Could not copy the link', 'danger');
    }
  };

  const columns: DataTableColumn<Invitation>[] = [
    {
      key: 'email',
      header: 'Email',
      render: (inv) => <span className="text-sm font-medium text-heading">{inv.email}</span>,
    },
    {
      key: 'role',
      header: 'Role',
      render: (inv) => (
        <span className="text-sm text-body">
          {ASSIGNABLE_ROLES.find((r) => r.value === inv.role)?.label ?? roleLabel(inv.role)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (inv) => (
        <Badge variant={STATUS[inv.status].variant} dot>
          {STATUS[inv.status].label}
        </Badge>
      ),
    },
    {
      key: 'sent',
      header: 'Sent',
      render: (inv) => <span className="text-sm text-muted">{formatDate(inv.createdAt)}</span>,
    },
    {
      key: 'expires',
      header: 'Expires',
      render: (inv) => <span className="text-sm text-muted">{formatDate(inv.expiresAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '320px',
      render: (inv) =>
        inv.status === 'pending' ? (
          <Can policy="CanInviteUsers">
            <div className="flex items-center justify-end gap-1.5">
              {inv.token && (
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={`Copy the invitation link for ${inv.email}`}
                  onClick={() => inv.token && copyLink(inv.token)}
                >
                  <Link2 size={14} aria-hidden="true" />
                  Copy link
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                loading={resend.isPending && resend.variables?.path.id === String(inv.id)}
                onClick={() => resend.mutate({ path: { id: String(inv.id) } })}
              >
                <RotateCw size={14} aria-hidden="true" />
                Resend
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={revoke.isPending && revoke.variables?.path.id === String(inv.id)}
                onClick={() => revoke.mutate({ path: { id: String(inv.id) } })}
              >
                <XCircle size={14} aria-hidden="true" />
                Revoke
              </Button>
            </div>
          </Can>
        ) : null,
    },
  ];

  if (isError) {
    return (
      <Callout
        tone="danger"
        title="Could not load invitations"
        actions={
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Callout>
    );
  }

  return (
    <DataTable
      framed
      columns={columns}
      rows={data?.data ?? []}
      rowKey={(inv) => inv.id}
      loading={isPending}
      aria-label="Invitations"
      empty={
        <EmptyState
          icon={Send}
          title="No invitations yet"
          description="People you invite by email appear here until they accept, so you can resend or revoke the ones still waiting."
        />
      }
    />
  );
};
